import * as React from "react";

import {
  Stage,
  Text,
} from "react-pixi-fiber";

import {
  IAlignedWord,
} from "../../api";

import Background from "./Background";
import Bookmarks from "./Bookmarks";
import Playhead from "./Playhead";
import Ruler from "./Ruler";
import Scaler, { IZoomProps } from "./Scaler";
import WordSpan from "./WordSpan";

interface IProps {
  alignment: IAlignedWord[],
  currentTime: number,
  duration: number | null,
  expanded: boolean,
  isPlaying: boolean,
  isSeeking: boolean,
  onSeek: (t: number) => void,
  recordingId: string | null,
}

interface IState {
  height: number,
  offsetX: number,
  offsetY: number,
  width: number,
  zoom: number,
}

const DEFAULT_ZOOM = 40;

export default class Waveform extends React.Component<IProps, IState> {

  public state: IState = {
    height: 0,
    offsetX: 0,
    offsetY: 0,
    width: 0,
    zoom: DEFAULT_ZOOM,
  }

  private container: HTMLDivElement | null;
  private scaler: Scaler | null;

  public componentWillMount() {
    window.addEventListener('resize', this.onResize);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  public componentDidMount() {
    this.onResize();
  }

  public componentWillUpdate(nextProps: IProps) {
    if (this.props.recordingId !== nextProps.recordingId) {
      this.setState({
        offsetX: 0,
        offsetY: 0,
        zoom: DEFAULT_ZOOM,
      })
    }
  }

  public componentWillReceiveProps(nextProps: IProps) {
    if (!this.scaler) {
      return;
    }
    const {bounds} = this.scaler;
  
    if (bounds) {
      // Detect if we've crossed to the next page and scroll
      const wasOnPage = (this.props.currentTime ? this.props.currentTime : 0) < bounds.max.x;
      const nowOffPage = (nextProps.currentTime ? nextProps.currentTime : 0) > bounds.max.x;
      const crossedPage = wasOnPage && nowOffPage;

      const justOpened = !this.props.expanded && nextProps.expanded;

      const isSeeking = this.props.isSeeking && nowOffPage;

      if (nextProps.isPlaying && (crossedPage || justOpened || isSeeking)) {
        const t = this.props.currentTime ? this.props.currentTime - 1 : 0;
        this.scrollTo(t);
      }
    }
  }

  public render() {
    const {
      height,
      offsetX,
      offsetY,
      width,
      zoom,
    } = this.state;
    const {
      alignment,
      currentTime,
      duration,
      expanded,
      onSeek,
      recordingId,
    } = this.props;

    const visibleWords = alignment.filter(this.isWordVisible);

    return (<div
      ref={ el => this.container = el }
      onWheel={this.onWheel}
      className={'waveform' + (expanded ? ' expanded' : '')}>
      <Stage
        width={width}
        height={height}
        options={{
          backgroundColor: 0xFFDDDDDD,
          resolution: window.devicePixelRatio,
          sharedTicker: true,
        }}>
        <Scaler
          duration={duration}
          width={width}
          height={height}
          ref={this.onScalerMount}
          onClick={onSeek}
          offsetX={offsetX}
          offsetY={offsetY}
          onZoom={this.onZoom}
          zoom={zoom}>
          <Background
          duration={duration} />
          { visibleWords.map(this.renderWordSpan) }
        </Scaler>
        { zoom > 50 ? visibleWords.map(this.renderText) : null }
        <Ruler
          bounds={this.scaler ? this.scaler.bounds : null}
          zoom={zoom}
          offsetX={offsetX}
          duration={duration || 0} />
        <Bookmarks
            recordingId={recordingId}
            zoom={zoom}
            offsetX={offsetX}
            duration={duration} />
        <Playhead
            zoom={zoom}
            offsetX={offsetX}
            currentTime={currentTime} />
      </Stage>
    </div>);
  }

  private onResize = () => {
    if (!this.container) {
      return;
    }

    const {clientWidth, clientHeight} = this.container;
    this.setState({
      height: clientHeight,
      width: clientWidth,
    });
  }

  private renderWordSpan = (word: IAlignedWord) => {
    return (<WordSpan
      key={`${word.word}-${word.start}-span`}
      start={word.start}
      duration={word.duration}
      zoom={this.state.zoom}
      fill={0xFFA500} />);
  }

  private renderText = (word: IAlignedWord) => {
    const {
      zoom,
      offsetX,
    } = this.state;
    return (<Text
      key={`${word.word}-${word.start}-text`}
      text={word.word}
      x={ zoom * word.start + offsetX }
      y={35}
      style={styles.text} />);
  }

  private isWordVisible = (word: IAlignedWord) => {
    if (!this.scaler) { return true }

    const {bounds} = this.scaler;
    if (!bounds) { return true; }

    const padding = 5;

    const visibleStart = bounds.min.x;
    const visibleEnd = bounds.max.x;

    const wordStart = word.start;
    const wordEnd = word.start + word.duration;
    
    if (wordEnd < (visibleStart - padding)) {
      return false;
    }
    if (wordStart > (visibleEnd + padding)) {
      return false;
    }

    return true;
  }

  private onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!this.scaler) { return; }
    this.scaler.onWheel(e);
  }

  private onScalerMount = (el: Scaler | null) => {
    if (!el) { return; }
    this.scaler = el;
  }

  private onZoom = ({offsetX, zoom}: IZoomProps) => {
    this.setState({
      offsetX,
      zoom,
    });
  }

  private scrollTo(t: number) {
    this.setState({
      offsetX: -t * this.state.zoom
    });
  }

}

const styles = {
  text: {
    fontSize: 9,
  } as PIXI.TextStyle,
}



// WEBPACK FOOTER //
// ./src/components/Player/Waveform.tsx